import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Stack,
  useTheme,
} from "@mui/material";
import { useNavigate } from "@tanstack/react-router";
import { useState, useCallback, useEffect, useRef } from "react";

import type { DragonTileSelection } from "@/components/DragonTilePicker/types";
import type { ChannelMedium, ReturnTo } from "@/types/dragon-tiles";

import { DragonTilePicker } from "@/components/DragonTilePicker/DragonTilePicker";
import { connectorReturnSearch } from "@/features/DragonTiles/connectorTileSelectReturn";
import { isTruePlaintextEmailEnabledForSite } from "@/features/DragonTiles/truePlaintextFlag";
import { EmailOutlinedIcon, SmsOutlinedIcon } from "@/icons";
import dayjs from "@/utils/configuredDayjs";

/** Email editor options; the value travels in the `editor` search param. */
const EMAIL_EDITORS = [
  {
    value: "dnd",
    label: "Drag and Drop",
    description: "Design visually with blocks, images, and products",
  },
  {
    value: "letter",
    label: "Letter Style",
    description: "A simple, personal-looking note",
  },
  {
    value: "true_plaintext",
    label: "Plain Text",
    description: "Pure text, best deliverability",
  },
  {
    value: "code",
    label: "Custom Code",
    description: "Write the email's HTML yourself",
  },
] as const;

type EmailEditor = (typeof EMAIL_EDITORS)[number]["value"];

interface CreativeSphereSearchParams {
  medium?: ChannelMedium;
  dragon_tile?: string;
  event?: string;
  order_by?: "create_date" | "modify_date";
  order?: "asc" | "desc";
  search?: string;
  view?: "grid" | "table";
}

interface CreateFrameModalProps {
  open: boolean;
  onClose: () => void;
  siteId: string;
  // Optional identifiers when not using the parent tile field
  parentTileId?: string;
  innerTileId?: string;
  // When true, show a dropdown to pick a parent tile and derive inner tile
  showParentTileField?: boolean;
  forcedLayoutType?: "email" | "text" | null;
  returnTo?: ReturnTo;
  connectorParentTileId?: string;
  selectedEventId?: string | null;
  selectedAudienceId?: string | null;
  tileSelectReturn?: {
    medium: "email" | "sms";
    insertAtIndex?: number;
  };
  // Creative Sphere search params to persist when returning
  creativeSphereParams?: CreativeSphereSearchParams;
  /** Called right before the modal navigates to the tile editor. */
  onBeforeNavigate?: () => void;
}

export function CreateFrameModal({
  open,
  onClose,
  siteId,
  parentTileId,
  innerTileId,
  showParentTileField = false,
  forcedLayoutType,
  returnTo,
  connectorParentTileId,
  selectedEventId,
  selectedAudienceId,
  tileSelectReturn,
  creativeSphereParams,
  onBeforeNavigate,
}: CreateFrameModalProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [frameName, setFrameName] = useState(
    dayjs().format("MM/DD/YYYY h:mm A"),
  );
  const [selectedLayout, setSelectedLayout] = useState<"email" | "text" | null>(
    forcedLayoutType ?? "email",
  );
  const [selectedEditor, setSelectedEditor] = useState<EmailEditor>("dnd");
  // Step 2 (editor choice) only exists for the email channel.
  const [step, setStep] = useState<1 | 2>(1);
  const [pickerKey, setPickerKey] = useState(0);
  const [selection, setSelection] = useState<DragonTileSelection>({
    dragonTileId: "",
    innerTileId: undefined,
    hasError: false,
    isConnector: false,
    connectorId: undefined,
    selectedEventId: null,
  });

  const resetModalState = useCallback(() => {
    setFrameName(dayjs().format("MM/DD/YYYY h:mm A"));
    setSelectedLayout(forcedLayoutType ?? "email");
    setSelectedEditor("dnd");
    setStep(1);
    setSelection({
      dragonTileId: "",
      innerTileId: undefined,
      hasError: false,
      isConnector: false,
      connectorId: undefined,
      selectedEventId: null,
    });
    setPickerKey((k) => k + 1);
  }, [forcedLayoutType]);

  const wasOpenRef = useRef(open);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      resetModalState();
    }
    wasOpenRef.current = open;
  }, [open, resetModalState]);

  const resolvedLayout = forcedLayoutType ?? selectedLayout;

  const handleSelectionChange = useCallback(
    (newSelection: DragonTileSelection) => {
      setSelection(newSelection);
    },
    [],
  );

  const handleContinue = () => {
    const variant = resolvedLayout === "text" ? "text" : "email";

    // Email gets a second step to pick the editor; text creates right away.
    if (variant === "email" && step === 1) {
      setStep(2);
      return;
    }

    // Decide which ids to send based on prop
    const navParentTileId = showParentTileField
      ? selection.dragonTileId || undefined
      : parentTileId;
    const navInnerTileId = showParentTileField
      ? selection.innerTileId || undefined
      : innerTileId;

    const resolvedSelectedEventId = showParentTileField
      ? selection.selectedEventId
      : selectedEventId;
    const resolvedConnectorParentTileId = showParentTileField
      ? selection.connectorId
      : connectorParentTileId;

    onClose();
    onBeforeNavigate?.();
    void navigate({
      to: "/dragon-tiles/$siteId/new/$variant",
      params: { siteId, variant },
      search: {
        tileName: frameName,
        parentTileId: navParentTileId || "",
        innerTileId: navInnerTileId || "",
        ...(variant === "email" && selectedEditor !== "dnd"
          ? { editor: selectedEditor }
          : {}),
        ...(returnTo === "connector-tile"
          ? connectorReturnSearch({
              returnTo,
              connectorParentTileId: resolvedConnectorParentTileId,
              selectedEventId: resolvedSelectedEventId,
              selectedAudienceId,
              ...(tileSelectReturn
                ? {
                    medium: tileSelectReturn.medium,
                    insertAtIndex: tileSelectReturn.insertAtIndex,
                  }
                : {}),
            })
          : resolvedSelectedEventId
            ? { selectedEventId: resolvedSelectedEventId }
            : {}),
        ...(returnTo ? { returnTo } : {}),
        ...(returnTo === "creative-sphere" && creativeSphereParams
          ? {
              medium: creativeSphereParams.medium,
              dragon_tile: creativeSphereParams.dragon_tile,
              event: creativeSphereParams.event,
              order_by: creativeSphereParams.order_by,
              order: creativeSphereParams.order,
              search: creativeSphereParams.search,
              view: creativeSphereParams.view,
            }
          : {}),
      },
    });
  };

  // A Dragon tile or the explicit "Unassigned" choice is required, so an
  // untiled creative is deliberate rather than a blank picker.
  const hasParentChoice = !!selection.dragonTileId || !!selection.isUnassigned;
  const isParentSelectionInvalid =
    showParentTileField && (!hasParentChoice || selection.hasError);

  const isContinueDisabled =
    !frameName.trim() || !resolvedLayout || isParentSelectionInvalid;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: "638px",
            maxWidth: "638px",
            borderRadius: "16px",
            border: `1px solid ${theme.palette.primary.main}`,
            backgroundColor: "#fffffb",
          },
        },
      }}
    >
      <DialogContent sx={{ p: 11, pb: 8 }}>
        {/* Step 1 stays mounted while step 2 shows, so picker state survives Back */}
        <Box sx={{ display: step === 1 ? undefined : "none" }}>
          <Stack spacing={4}>
            {/* Title and Description */}
            <Stack spacing={3}>
              <Typography variant="h6" color="text.primary">
                Create new tile
              </Typography>
              {forcedLayoutType && (
                <Typography variant="body1" color="text.primary">
                  Start by giving your {forcedLayoutType} tile a name.
                </Typography>
              )}
              {!forcedLayoutType && (
                <Typography variant="body1" color="text.primary">
                  Start by giving your tile a name and choosing the first
                  message type.
                </Typography>
              )}
            </Stack>

            {/* Input Field */}
            <Typography variant="body2">Tile name</Typography>
            <TextField
              value={frameName}
              onChange={(e) => setFrameName(e.target.value)}
              placeholder="Type name"
              variant="outlined"
              sx={{ width: "460px" }}
            />
          </Stack>

          {/* Optional Parent Tile Selection */}
          {showParentTileField && (
            <DragonTilePicker
              key={pickerKey}
              siteId={siteId}
              enabled={open && showParentTileField}
              includeConnectors={showParentTileField}
              allowUnassigned
              onSelectionChange={handleSelectionChange}
              label="Dragon tile"
              spacing={3}
              sx={{ mt: 6 }}
            />
          )}

          {/* Layout Type Selection */}
          {!forcedLayoutType && (
            <Stack spacing={4} sx={{ mt: 8 }}>
              <Typography variant="subtitle1" color="text.primary">
                Choose channel
              </Typography>
              <Typography variant="body1" color="text.primary">
                Select how this tile will be sent:
              </Typography>

              <Stack direction="row" spacing={5}>
                {/* Email Option */}
                <Box
                  component="button"
                  type="button"
                  aria-pressed={selectedLayout === "email"}
                  onClick={() => setSelectedLayout("email")}
                  sx={{
                    flex: 1,
                    cursor: "pointer",
                    border:
                      selectedLayout === "email"
                        ? `2px solid ${theme.palette.primary.main}`
                        : `2px solid ${theme.palette.grey[400]}`,
                    borderRadius: "8px",
                    bgcolor: "transparent",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "120px",
                    position: "relative",
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                      "& .icon": {
                        color: theme.palette.primary.main,
                      },
                      "& .text": {
                        color: theme.palette.primary.main,
                      },
                    },
                    "&:focus-visible": {
                      "& .icon": {
                        color: theme.palette.primary.main,
                      },
                      "& .text": {
                        color: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 80,
                      height: 80,
                      mb: 2,
                    }}
                  >
                    <EmailOutlinedIcon
                      className="icon"
                      sx={{
                        fontSize: 48,
                        color:
                          selectedLayout === "email"
                            ? theme.palette.primary.main
                            : theme.palette.text.primary,
                      }}
                    />
                  </Box>
                  <Typography
                    className="text"
                    variant="subtitle1"
                    color={
                      selectedLayout === "email"
                        ? theme.palette.primary.main
                        : theme.palette.text.primary
                    }
                  >
                    Email
                  </Typography>
                </Box>

                {/* Text Option */}
                <Box
                  component="button"
                  type="button"
                  aria-pressed={selectedLayout === "text"}
                  onClick={() => setSelectedLayout("text")}
                  sx={{
                    flex: 1,
                    cursor: "pointer",
                    border:
                      selectedLayout === "text"
                        ? `2px solid ${theme.palette.primary.main}`
                        : `2px solid ${theme.palette.grey[400]}`,
                    borderRadius: "8px",
                    bgcolor: "transparent",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "120px",
                    position: "relative",
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                      "& .icon": {
                        color: theme.palette.primary.main,
                      },
                      "& .text": {
                        color: theme.palette.primary.main,
                      },
                    },
                    "&:focus-visible": {
                      "& .icon": {
                        color: theme.palette.primary.main,
                      },
                      "& .text": {
                        color: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 80,
                      height: 80,
                      mb: 2,
                    }}
                  >
                    <SmsOutlinedIcon
                      className="icon"
                      sx={{
                        fontSize: 48,
                        color:
                          selectedLayout === "text"
                            ? theme.palette.primary.main
                            : theme.palette.text.primary,
                      }}
                    />
                  </Box>
                  <Typography
                    className="text"
                    variant="subtitle1"
                    color={
                      selectedLayout === "text"
                        ? theme.palette.primary.main
                        : theme.palette.text.primary
                    }
                  >
                    Text
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          )}
        </Box>

        {/* Step 2: editor choice (email only) */}
        {step === 2 && (
          <Stack spacing={4}>
            <Typography variant="h6" color="text.primary">
              Pick your editor
            </Typography>
            <Typography variant="body1" color="text.primary">
              How do you want to build this email?
            </Typography>

            <Stack spacing={3}>
              {EMAIL_EDITORS.filter(
                (option) =>
                  option.value !== "true_plaintext" ||
                  isTruePlaintextEmailEnabledForSite(siteId),
              ).map((option) => {
                const isSelected = selectedEditor === option.value;
                return (
                  <Box
                    key={option.value}
                    component="button"
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => setSelectedEditor(option.value)}
                    sx={{
                      cursor: "pointer",
                      border: isSelected
                        ? `2px solid ${theme.palette.primary.main}`
                        : `2px solid ${theme.palette.grey[400]}`,
                      borderRadius: "8px",
                      bgcolor: "transparent",
                      display: "flex",
                      alignItems: "baseline",
                      gap: 3,
                      padding: "12px 16px",
                      textAlign: "left",
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                        "& .title": { color: theme.palette.primary.main },
                      },
                      "&:focus-visible": {
                        "& .title": { color: theme.palette.primary.main },
                      },
                    }}
                  >
                    <Typography
                      className="title"
                      variant="subtitle2"
                      color={
                        isSelected
                          ? theme.palette.primary.main
                          : theme.palette.text.primary
                      }
                    >
                      {option.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: "auto" }}
                    >
                      {option.description}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Stack>
        )}
      </DialogContent>

      {/* Action Buttons */}
      <DialogActions sx={{ px: 11, pb: 11, pt: 0, justifyContent: "flex-end" }}>
        <Stack direction="row" spacing={3}>
          <Button
            onClick={step === 2 ? () => setStep(1) : onClose}
            variant="outlined"
            sx={{
              ...theme.typography.button,
              textTransform: "uppercase",
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              borderRadius: "100px",
              padding: "8px 22px",
              minHeight: "39px",
              "&:hover": {
                borderColor: theme.palette.primary.main,
                bgcolor: "transparent",
              },
            }}
          >
            {step === 2 ? "Back" : "Cancel"}
          </Button>
          <Button
            onClick={() => void handleContinue()}
            variant="contained"
            disabled={isContinueDisabled}
            sx={{
              ...theme.typography.button,
              textTransform: "uppercase",
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              borderRadius: "100px",
              padding: "8px 22px",
              minHeight: "39px",
              boxShadow: "none",
              "&:hover": {
                bgcolor: theme.palette.primary.dark,
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                bgcolor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
            }}
          >
            Continue
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
